import { Flex } from "@chakra-ui/layout";
import { Box, Grid } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useCallback } from "react";
import { AddressAutocomplete } from "src/components/Inputs/Address/Autocomplete/AddressAutocompleteInput";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import {
  PersonAddress,
  PersonAddressForm,
  SchoolAddress,
  SchoolAddressForm,
} from "src/schemas/Address";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";

export type Props = {
  isRequired?: boolean;
  isDisabled?: boolean;
  fieldPrefix?: string;
  autoFocus?: boolean;
  // Opt in to forcing City / State / Zip onto a single non-wrapping row. Off by
  // default so existing consumers keep the responsive auto-fit layout.
  cityStateZipSingleRow?: boolean;
};

export const AddressForm: React.FC<Props> = (props) => {
  const {
    isRequired,
    isDisabled,
    fieldPrefix,
    autoFocus,
    cityStateZipSingleRow,
  } = props;
  const formik = useFormikContext<PersonAddressForm | SchoolAddressForm>();

  const withPrefix = useCallback(
    (name: string) => {
      if (!fieldPrefix) {
        return name;
      }

      return [fieldPrefix, name].join(".");
    },
    [fieldPrefix]
  );

  const setAddressFields = (address: PersonAddress | SchoolAddress) => {
    for (const [key, value] of Object.entries(address)) {
      formik.setFieldValue(withPrefix(key), value ?? "");
    }

    setTimeout(() => {
      formik.validateForm();
    });
  };

  const streetField = (
    <AddressAutocomplete onAddressSelected={setAddressFields}>
      <InputWithValidationControl
        isRequired={isRequired}
        label="Address"
        inputProps={{
          placeholder: "E.g.: 532 Oakland Ave",
          autoFocus,
          ...PASSWORD_MANAGER_IGNORE,
        }}
        name={withPrefix("street_address")}
        id="street_address"
        isDisabled={isDisabled}
      />
    </AddressAutocomplete>
  );
  const aptField = (
    <InputWithValidationControl
      isRequired={false}
      inputProps={{ placeholder: "E.g.: 5B", ...PASSWORD_MANAGER_IGNORE }}
      name={withPrefix("street_address_line_2")}
      id="street_address_line_2"
      label="Apartment/suite (Optional)"
      isDisabled={isDisabled}
    />
  );
  const cityField = (
    <InputWithValidationControl
      isRequired={isRequired}
      inputProps={{
        placeholder: "E.g.: New Orleans",
        ...PASSWORD_MANAGER_IGNORE,
      }}
      name={withPrefix("city")}
      id="city"
      label="City"
      isDisabled={isDisabled}
    />
  );
  const stateField = (
    <InputWithValidationControl
      isRequired={isRequired}
      inputProps={{ placeholder: "E.g.: LA", ...PASSWORD_MANAGER_IGNORE }}
      name={withPrefix("state")}
      id="state"
      label="State"
      isDisabled={isDisabled}
    />
  );
  const zipField = (
    <InputWithValidationControl
      isRequired={isRequired}
      inputProps={{
        placeholder: "E.g.: 10181",
        ...PASSWORD_MANAGER_IGNORE,
      }}
      name={withPrefix("zip_code")}
      id="zip_code"
      label="Zip code"
      isDisabled={isDisabled}
    />
  );

  if (cityStateZipSingleRow) {
    return (
      <Flex direction="column" gap={3}>
        <Grid
          gridTemplateColumns="repeat(auto-fit, minmax(15rem, 1fr))"
          alignItems="flex-end"
          gap={{ md: 3 }}
        >
          {streetField}
          {aptField}
        </Grid>
        {/* City is wider than the shorter State and Zip fields. */}
        <Flex gap={3} alignItems="flex-end">
          <Box flex="2">{cityField}</Box>
          <Box flex="1">{stateField}</Box>
          <Box flex="1">{zipField}</Box>
        </Flex>
      </Flex>
    );
  }

  return (
    <Grid
      gridTemplateColumns="repeat(auto-fit, minmax(15rem, 1fr))"
      alignItems="flex-end"
      gap={{ md: 3 }}
    >
      {streetField}
      {aptField}
      {cityField}
      <Flex gap={3} alignItems="flex-end">
        {stateField}
        {zipField}
      </Flex>
    </Grid>
  );
};
